import React from 'react';
import mapValues from 'lodash/mapValues';
import decorateComponentWithProps from 'decorate-component-with-props';
import { withTranslation, WithTranslation } from 'react-i18next';
import { ModuleType } from '../../../../types/modules';
import OnScrollToElement from '@wix/da-shared-react/pkg/OnScrollToElement';
import { withMobileContext } from '@wix/da-react-context/pkg/MobileContext';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import Zone, { ZoneGroup } from '../../Layout/Zone';
import { ZoneType } from '../../../../types/layout';

import WidgetNavMenu from '../../UserWidgets/_partials/WidgetNavMenu';
import withSectionFetchSpinner from '../../../decorators/withSectionFetchSpinner';
import SideNavBar from '../../SideNavBar';

import profilesCssVars from '../../../styles/variables.scss';
const scrollToChildOffsetTop = parseInt(
  profilesCssVars['navbar-sticky-height']
);
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import { ModuleTypeToWidgetComponentMap } from './constants';
import WideWidgetLayout from '../../Layout/WideWidgetLayout';

import s from './AboutSection.scss';

export interface Props {
  stats: object;
  routes: { hash: string; transKey: string; count: string }[];
  layoutVersion?: number;
  isMobile?: boolean;
}

export interface State {
  activeHash: string;
}
const defaultState = {
  activeHash: ModuleType.ABOUT,
};

class AboutSection extends React.Component<Props & WithTranslation, State> {
  state = defaultState;
  zoneProps;
  widgetRefs;

  render() {
    const { activeHash } = this.state;
    const { t } = this.props;
    return (
      <WideWidgetLayout
        className={s['hover-control']}
        navigation={
          <SideNavBar
            routes={this.props.routes}
            activeHash={activeHash}
            onActiveHashChange={this.updateActiveHash}
            className={s['hide-mobile']}
            biEvid={BiEventType.PROFILE_ABOUT_SIDEBAR}
            heading={
              <VisuallyHidden>
                <Heading>{t('pages.about.navmenu.a11y.heading')}</Heading>
              </VisuallyHidden>
            }
          />
        }
      >
        <ZoneGroup>
          <Zone type={ZoneType.TOP_RIGHT} {...this.zoneProps} locked={true} />
          <Zone type={ZoneType.MID_RIGHT} {...this.zoneProps} />
          <Zone type={ZoneType.BOTTOM_RIGHT} {...this.zoneProps} />
        </ZoneGroup>
        <ErrorBoundary hideOnFail>
          <OnScrollToElement
            elements={this.widgetRefs}
            offsetTop={scrollToChildOffsetTop}
            onScrollTo={this.updateActiveElement}
          />
        </ErrorBoundary>
      </WideWidgetLayout>
    );
  }

  renderWidgetNavMenu() {
    const { isMobile } = this.props;
    if (!isMobile) {
      return null;
    }
    return <WidgetNavMenu widgetMenuItemsList={this.props.routes} />;
  }

  updateActiveElement = (elem: HTMLElement) => {
    this.updateActiveHash(elem.id);
  };

  updateActiveHash = (activeHash: string) => {
    const documentLocationHash =
      document && document.location && document.location.hash;
    if (!documentLocationHash && activeHash === defaultState.activeHash) {
      return;
    }

    const hash = `#${activeHash}`;
    if (documentLocationHash !== hash) {
      this.setState({ activeHash });
      history.replaceState({}, '', hash);
    }
  };

  UNSAFE_componentWillMount() {
    this.attachPropsToWidgets();
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextProps.layoutVersion !== this.props.layoutVersion) {
      this.attachPropsToWidgets();
      return true;
    }
    return nextState.activeHash !== this.state.activeHash;
  }

  attachPropsToWidgets() {
    // this code used to be a part of the render method and its purpose was to initialize
    // the mapModuleTypeToWidgetComponent map and inject some props to each of the widgets.
    // These props were needed for OnScrollToElement to work properly, but since this
    // was inside the render method, every time the state changed this code was executed, and
    // a new mapModuleTypeToWidgetComponent map was generated with a new set of references.
    // As a consequence, this was causing a re-render of each and every widget in the map.
    // Moving this logic into a separate method, and making it run only once per layoutVersion
    // solved the re-rendering issue.

    const moduleProps = {
      isHeaderStickyOnMobile: true,
      headerExtraElementsOnMobile: this.renderWidgetNavMenu(),
      widgetBoxClassName: s['widget'],
    };
    const refs = [] as HTMLElement[];
    const mapModuleTypeToWrappedWidgetComponent = mapValues(
      ModuleTypeToWidgetComponentMap,
      (WidgetComponent, moduleType) =>
        decorateComponentWithProps(WidgetComponent, {
          id: moduleType,
          parentRef: comp => {
            if (comp instanceof HTMLElement) {
              refs.push(comp);
            }
          },
          ...moduleProps,
        })
    );

    this.widgetRefs = refs;
    this.zoneProps = {
      mapModuleTypeToWidgetComponent: mapModuleTypeToWrappedWidgetComponent,
      className: s['zone'],
    };
  }
}

export default withMobileContext(
  withSectionFetchSpinner(withTranslation()(AboutSection))
);
